import React from 'react';
import Style from './index.less';
import classNames from 'classnames';
import Loading from '@tencent/dui/lib/components/Loading';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { Application, ImageFill, Color, ImageResource, SvgGraphics } from '@tencent/alloy-renderer';
import { replaceSdkThemeColor } from '@src/framework/editor/src/js/utils/color';
import { ColorAdjustmentFilter } from '@tencent/alloy-renderer-tdoc';
import { ImageFilter } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/filter/image-filter';
import cloneDeep from 'lodash/cloneDeep';
import { ImagePropsField } from '@src/slide/domain/ui-model/element-modeles/property/image-operation/constant/props-value';
import { ReColorStyleList } from './constans';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import { getAllImage } from '@src/slide/application/service/menu-manager/menu-values/element-menu-values-getter';
import { RecolorCache } from './recolor-cache';
import { STSchemeColorVal } from '@tencent/slide-sdk-model';
import { getAllChildModels } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { isImageOrImageFillShape } from '@src/slide/domain/ui-model/elements/element-judge';


const RECOLOR_ITEM_WIDTH = 60;

interface Luminance {
    brightness: number;
    contrast: number;
}

interface DuotoneColor {
    colorType: string,
    colorValue: STSchemeColorVal | string,
    tint?: number,
    shade?: number,
    satMod?: number,
    srgb_color?: string,
}

export interface ReColorStyleItem {
    style: {
        grayscl?: boolean;
        biLevel?: number;
        duotone?: {
            clr1: DuotoneColor;
            clr2: DuotoneColor;
            color1?: Color,
            color2?: Color,
        };
        luminance?: {
            brightness?: number;
            contrast?: number;
        }
    };
    key: string;
    title: string;
}

interface RecolorDropDownProps {
    model: IPictureModel,
    onRecolorStyleClick: (recolorProps) => void;
    /** 是否在工具栏， 应用工具栏的样式 */
    fromToolbar?: boolean;
    onRecolorStyleMouseEnter?: (value) => void;
    onRecolorStyleMouseLeave?: () => void;
}

interface RecolorDropDownState {
    loading: boolean;
}

export default class RecolorPresetMenu extends React.Component<RecolorDropDownProps, RecolorDropDownState> {
    public ReColorPresetDropList;
    private appliction: Application;
    private imageSrc: string;
    private otherFilterProps = {};
    private imageModel: IPictureModel;

    public constructor(props, context) {
        super(props, context);
        this.ReColorPresetDropList = Array(ReColorStyleList.length).fill('');
        const selectedModels = getAllChildModels(this.props.model as IElementBaseModel);
        this.imageModel = selectedModels.find(isImageOrImageFillShape) as IPictureModel;
        const model = this.imageModel;
        this.imageSrc = this.imageModel.props.src ?? model.props.style.fillBackground?.image?.src;
        const pictureCache = RecolorCache.getInstance().getFromCache(model.key);
        this.state = {
            loading: !pictureCache,
        };
        if (pictureCache) {
            // 优先用缓存中的数据
            this.ReColorPresetDropList = pictureCache;
        } else {
            this.generateRecolorIcon();
        }
    }

    public componentWillUnmount() {
        this.appliction?.dispose();
    }

    public getUpdateStyleItem(key: string) {
        const item = cloneDeep(ReColorStyleList.find(item => item.key === key)) as any;
        if (!item) return;
        const { grayscl, duotone, biLevel, luminance } = item.style;
        if (!grayscl) {
            item.style.grayscl = null;
        }
        // sdk在应用数据时会去掉undefined，设置为null保证效果
        if (!duotone) {
            item.style.duotone = null;
        }

        if (!biLevel) {
            item.style.biLevel = null;
        }

        if (!luminance) {
            item.style.luminance = null;
        }
        return item;
    }

    public handleRecolorIconClick = (key: string) => {
        const item = this.getUpdateStyleItem(key);
        this.ReColorPresetDropList.forEach((item) => {
            if (item.key === key) {
                item.isSelected = true;
            } else {
                item.isSelected = false;
            }
        });
        this.props.onRecolorStyleClick(item.style);
    };

    public handleRecolorIconMouseEnter = (key: string) => {
        const item = this.getUpdateStyleItem(key);
        this.props.onRecolorStyleMouseEnter?.(item.style);
    };

    public handleRecolorIconMouseLeave = () => {
        this.props.onRecolorStyleMouseLeave?.();
    };

    public render() {
        return (
            <Loading visible={this.state.loading}>
                <div className={this.props.fromToolbar ? Style.toolbarContainer : Style.container} >
                    <div className={Style['recolor-collection']} style={this.props.fromToolbar ? {} : { height: this.getCollectionHeight() }}>
                        {
                            this.ReColorPresetDropList.map((item) => {
                                const recolorItemClass = classNames({
                                    [Style['recolor-collection-item']]: true,
                                    [Style['recolor-item-selected']]: item.isSelected,
                                });
                                return (
                                    <Tooltip key={item.key} placement={['top', 'right']} title={item.title}>
                                        <div
                                            className={recolorItemClass}
                                            onClick={() => {
                                                this.handleRecolorIconClick(item.key);
                                            }}
                                            onMouseEnter={() => this.handleRecolorIconMouseEnter(item.key)}
                                            onMouseLeave={() => this.handleRecolorIconMouseLeave()}
                                        >
                                            <div className={Style['preset-icon']}>
                                                <img src={item.blobUrl}></img>
                                            </div>
                                        </div>
                                    </Tooltip>
                                );
                            })
                        }
                    </div>
                </div>
            </Loading>
        );
    }

    private async generateRecolorIcon() {
        const { image } = this.imageModel.props.style;
        if (image) {
            const { brightness, contrast, saturate, colorTemperature } = image;
            this.otherFilterProps = {
                brightness: ImageFilter.getBrightnessValue(brightness || ImagePropsField.BRIGHTNESS.defaultValue),
                contrast: ImageFilter.getContrastValue(contrast || ImagePropsField.CONTRAST.defaultValue),
                saturate: ImageFilter.getSaturateValue(saturate),
                temperature: colorTemperature,
            };
        }
        const target = new BrowserTarget();
        const canvasElement = document.createElement('canvas');
        this.appliction = new Application(target, { native: canvasElement });
        const imageResource = this.appliction.loader.loadImage(this.imageSrc, { crossOrigin: true });
        await new Promise<void>((resolve) => {
            imageResource.onImageLoaded(async () => {
                resolve();
            });
        });
        const { width: originWidth, height: originHeight } = imageResource;
        this.appliction.width = RECOLOR_ITEM_WIDTH;
        this.appliction.height = RECOLOR_ITEM_WIDTH * originHeight / originWidth;
        this.appliction.resolution = 1;
        const path = `M0 0 L${this.appliction.width} 0 L${this.appliction.width} ${this.appliction.height} L0 ${this.appliction.height} Z`;
        const styleList = cloneDeep(ReColorStyleList);
        for (let i = 0, len = styleList.length; i < len; i++) {
            await this.generateRecolorIconItem(styleList[i], path, canvasElement, imageResource, i);
        }
    }

    private generateRecolorIconItem(
        item: ReColorStyleItem,
        path: string,
        canvasElement: HTMLCanvasElement,
        image: ImageResource,
        index: number,
    ): Promise<void> {
        const graphics = new SvgGraphics(path);
        this.renderImgWithFilter(graphics, image, this.appliction, item);
        return new Promise<void>((resolve) => {
            canvasElement.toBlob((blob) => {
                if (!blob) return;
                const blobUrl = URL.createObjectURL(blob);
                const isSelected = this.isItemSelected(item);
                this.ReColorPresetDropList[index] = {
                    blobUrl,
                    key: item.key,
                    isSelected,
                    title: item.title,
                };
                graphics.dispose();
                this.appliction.stage.removeChild(graphics);
                if (this.isAllImageRendered()) {
                    this.setState({ loading: false });
                    RecolorCache.getInstance().addToCache(this.imageModel.key, this.ReColorPresetDropList);
                }
                resolve();
            });
        });
    }

    private isAllImageRendered(): boolean {
        let loaded = true;
        this.ReColorPresetDropList.forEach((item) => {
            if (!item.blobUrl) {
                loaded = false;
            }
        });
        return loaded;
    }

    private renderImgWithFilter(graphics: SvgGraphics, image: ImageResource, app: Application, item: ReColorStyleItem) {
        graphics.fill = new ImageFill(image);
        graphics.x = 0;
        graphics.y = 0;
        app.stage.addChild(graphics);
        const styleItem = this.getStyleItem(item);

        const filter = new ColorAdjustmentFilter({ ...this.otherFilterProps, ...styleItem.style });
        filter.forbidWebgl = false;
        graphics.addFilter(filter);
        app.render();
    }

    private getStyleItem(item: ReColorStyleItem) {
        const newItem = replaceSdkThemeColor(this.imageModel, item) as any;
        // 渲染层数据预处理
        if (newItem.style.duotone) {
            newItem.style.duotone.color1 = Color.fromHex(newItem.style.duotone.clr1.srgb_color!);
            newItem.style.duotone.color2 = Color.fromHex(newItem.style.duotone.clr2.srgb_color!);
        }
        if (newItem.style.luminance) {
            const { brightness } = newItem.style.luminance;
            const { contrast } = newItem.style.luminance;
            newItem.style.luminance.brightness = ImageFilter.getBrightnessValue(brightness!);
            newItem.style.luminance.contrast = ImageFilter.getContrastValue(contrast!);
        }
        return newItem;
    }

    private getCollectionHeight() {
        const { width, height } = this.imageModel.props.style;
        return ((RECOLOR_ITEM_WIDTH * height / width) * 3) + 56;
    }

    private isItemSelected(item: ReColorStyleItem): boolean {
        const { image } = this.imageModel.props.style;
        if (!image) return false;
        const { grayscl, duotone, biLevel, luminance } = image;
        if (grayscl) {
            return !!item.style.grayscl;
        }
        if (biLevel) {
            return item.style.biLevel === biLevel;
        }
        if (luminance) {
            return this.isLuminanceSelected(item, luminance);
        }

        if (duotone) {
            return this.isDuotoneSelected(item, duotone);
        }
        if (Object.keys(item.style).length === 0) {
            return true;
        }
        return false;
    }

    private isLuminanceSelected(
        item: ReColorStyleItem,
        luminance: Luminance,
    ) {
        return luminance.brightness === item.style.luminance?.brightness
        && luminance.contrast === item.style.luminance?.contrast;
    }

    private isDuotoneSelected(item: ReColorStyleItem, duotone) {
        return duotone.clr1.colorValue?.toLowerCase() === item.style.duotone?.color1?.hex?.toLowerCase()
        && duotone.clr2.colorValue?.toLowerCase() === item.style.duotone?.color2?.hex?.toLowerCase();
    }
}
